import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, ViewChild, OnDestroy, TemplateRef, Output, HostBinding, ViewContainerRef } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Observable, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap, catchError, takeUntil } from 'rxjs/operators';
import { tr } from '@util/tr';

export interface WsSearchResult {
  id: any;
  displayData: { [key: string]: any };
}

export interface WsSearchbarConfig {
  placeholder?: string;
  tooltip?: string;
  debounceTime?: number;
  minSearchLength?: number;
  showIcon?: boolean;
  expanded?: boolean;
  showSpinner?: boolean;
  variant?: 'default' | 'toolbar';
  animationDuration?: number; // Duration in milliseconds
  animationEasing?: string; // CSS easing function
  expandDirection?: 'left' | 'right' | 'auto'; // Direction to expand when opened
  /** When false, the autocomplete does not show the empty-state "No results" row (e.g. server-driven search only). */
  showNoResultsMessage?: boolean;
  /** Show inline clear (×) when expanded; clears text and collapses by default. */
  showClearButton?: boolean;
  /** When true (default), clicking clear also collapses the searchbar. */
  collapseOnClear?: boolean;
  /** Show clear (×) only after the user has performed a search (Enter or debounced input). */
  showClearButtonAfterSearch?: boolean;
  /** Collapse the searchbar on blur when the input is empty (click outside). */
  collapseOnBlurWhenEmpty?: boolean;
  /** Keep several results selected. The panel stays open and chips show the selection. */
  multiSelect?: boolean;
  /** `input` is a full-width form control. `default` is the toolbar pill. */
  appearance?: 'default' | 'input';
}

@Component({
  selector: 'ws-searchbar',
  templateUrl: './ws-searchbar.component.html',
  styleUrls: ['./ws-searchbar.component.scss'],
})
export class WsSearchbarComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() config: WsSearchbarConfig = {};
  @Input() searchFunction!: (searchText: string) => Observable<any[]>;
  @Input() resultMapper!: (item: any) => WsSearchResult;
  @Input() resultTemplate?: TemplateRef<any>;
  @Output() selectedItemsChange = new EventEmitter<any[]>();

  private _selectedItems: any[] = [];

  @Input()
  set selectedItems(value: any[] | null) {
    this._selectedItems = Array.isArray(value) ? [...value] : [];
  }

  get selectedItems(): any[] {
    return this._selectedItems;
  }

  // Track if expanded was initially set to true in the input config
  private initiallyExpanded: boolean = false;

  @HostBinding('class.with-clear-button')
  get hasClearButton(): boolean {
    return this.showClearIcon;
  }

  get showClearIcon(): boolean {
    if (!this.mergedConfig.showClearButton || !this.mergedConfig.expanded || this.initiallyExpanded) {
      return false;
    }
    if (this.mergedConfig.showClearButtonAfterSearch) {
      return this.searchCommitted && this.getInputText().length > 0;
    }
    return true;
  }

  @HostBinding('class.expanded')
  get isExpanded() {
    return this.mergedConfig.expanded;
  }

  @HostBinding('style.min-width')
  get dynamicMinWidth() {
    if (this.mergedConfig.appearance === 'input') {
      return '0';
    }
    if (this.mergedConfig.expanded) {
      // Calculate width based on placeholder length
      // Base width of 100px + ~8px per character + padding
      const placeholder = this.mergedConfig.placeholder || '';
      const baseWidth = 100;
      const charWidth = 8;
      const calculatedWidth = baseWidth + (placeholder.length * charWidth);
      const width = Math.min(Math.max(calculatedWidth, 200), 450);
      return `${width}px`;
    }
    return '40px'; // Collapsed state
  }

  @HostBinding('style.--animation-duration')
  get animationDuration() {
    return `${this.mergedConfig.animationDuration}ms`;
  }

  @HostBinding('style.--animation-easing')
  get animationEasing() {
    return this.mergedConfig.animationEasing;
  }

  @HostBinding('class')
  get hostClasses() {
    const classes = [];
    if (this.mergedConfig.expanded) {
      classes.push('expanded');
    }
    if (this.initiallyExpanded) {
      classes.push('permanently-expanded');
    }
    if (this.mergedConfig.expandDirection) {
      classes.push(`expand-${this.mergedConfig.expandDirection}`);
    }
    if (this.mergedConfig.appearance === 'input') {
      classes.push('appearance-input');
    }
    return classes.join(' ');
  }

  @Output() submit = new EventEmitter<string>();
  @Output() openStateToggled = new EventEmitter<boolean>();
  @Output() resultSelected = new EventEmitter<{ item: any; mappedResult: WsSearchResult }>();
  @Output() searchTextChanged = new EventEmitter<string>();

  private searchText$ = new Subject<string>();
  private searchCommit$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  private searchCommitted = false;
  inputFocused = false;
  private lastQuery = '';
  private latestItems: any[] = [];
  searchResults: WsSearchResult[] = [];
  loading = false;

  @ViewChild('searchBox') searchBox!: any;
  @ViewChild('autoTrigger') autoTrigger!: MatAutocompleteTrigger;
  @ViewChild('multiPanel') multiPanel!: TemplateRef<any>;

  private overlayRef: OverlayRef | null = null;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
  ) {}

  // Default configuration
  private defaultConfig: WsSearchbarConfig = {
    placeholder: tr('Search'),
    tooltip: '',
    debounceTime: 300,
    minSearchLength: 1,
    showIcon: true,
    expanded: false,
    showSpinner: true,
    variant: 'default',
    animationDuration: 300,
    animationEasing: 'ease-in-out',
    expandDirection: 'right',
    showNoResultsMessage: true,
    showClearButton: false,
    collapseOnClear: true,
    showClearButtonAfterSearch: false,
    collapseOnBlurWhenEmpty: false,
    multiSelect: false,
    appearance: 'default',
  };

  get mergedConfig(): WsSearchbarConfig {
    return { ...this.defaultConfig, ...this.config };
  }

  ngOnInit() {
    if (!this.searchFunction) {
      throw new Error('ws-searchbar: searchFunction is required');
    }
    if (!this.resultMapper) {
      throw new Error('ws-searchbar: resultMapper is required');
    }

    // Track if expanded was initially set to true
    this.initiallyExpanded = this.config.expanded === true;

    this.searchText$
      .pipe(
        distinctUntilChanged(),
        tap((searchText) => {
          if (this.mergedConfig.multiSelect) {
            this.loading = false;
            return;
          }
          if (searchText.length >= this.mergedConfig.minSearchLength!) {
            this.loading = this.mergedConfig.showSpinner!;
            this.searchResults = [];
          } else {
            this.searchResults = [];
            this.loading = false;
          }
        }),
        debounceTime(this.mergedConfig.debounceTime!),
        switchMap((searchText) => {
          if (searchText.length < this.mergedConfig.minSearchLength!) {
            return of([]);
          }
          return this.searchFunction(searchText).pipe(
            tap(() => (this.loading = false)),
            catchError((error) => {
              console.error('ws-searchbar search error:', error);
              this.loading = false;
              return of([]);
            })
          );
        })
      )
      .subscribe((results) => {
        this.latestItems = results || [];
        this.searchResults = this.latestItems
          .map((item) => this.safeMap(item))
          .sort((a, b) => this.resultLabel(a).localeCompare(this.resultLabel(b), undefined, { sensitivity: 'base' }));
      });

    this.searchCommit$
      .pipe(
        debounceTime(this.mergedConfig.debounceTime!),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((searchText) => {
        if (searchText.trim().length >= this.mergedConfig.minSearchLength!) {
          this.searchCommitted = true;
        }
      });
  }

  ngAfterViewInit() {
    if (this.mergedConfig.multiSelect && this.mergedConfig.minSearchLength === 0) {
      this.searchText$.next('');
    }
  }

  optionLabel(result: WsSearchResult): string {
    return this.resultLabel(result);
  }

  pickOption(id: any): void {
    this.toggleSelected(id);
  }

  private loadMultiOptions(searchText: string): void {
    let request: Observable<any[]>;
    try {
      request = this.searchFunction(searchText);
    } catch (error) {
      console.error('ws-searchbar search error:', error);
      request = of([]);
    }
    request.pipe(
      catchError((error) => {
        console.error('ws-searchbar search error:', error);
        return of([]);
      }),
      takeUntil(this.destroy$)
    ).subscribe((results) => {
      this.latestItems = results || [];
      this.searchResults = this.latestItems
        .map((item) => this.safeMap(item))
        .sort((a, b) => this.resultLabel(a).localeCompare(this.resultLabel(b), undefined, { sensitivity: 'base' }));
    });
  }

  private openMultiPanel(): void {
    if (!this.mergedConfig.multiSelect || !this.inputFocused || !this.multiPanel || this.overlayRef) {
      return;
    }
    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions([{
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
      }]);
    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      width: this.elementRef.nativeElement.getBoundingClientRect().width,
    });
    this.overlayRef.attach(new TemplatePortal(this.multiPanel, this.viewContainerRef));
  }

  private closeMultiPanel(): void {
    if (!this.overlayRef) {
      return;
    }
    this.overlayRef.dispose();
    this.overlayRef = null;
  }

  ngOnDestroy() {
    this.closeMultiPanel();
    this.destroy$.next();
    this.destroy$.complete();
    this.searchText$.complete();
    this.searchCommit$.complete();
  }

  toggleOpen() {
    // If expanded was explicitly set to true in initial config, prevent toggling (always stay open)
    if (this.initiallyExpanded) {
      return;
    }

    // Toggle the expanded state directly in the config
    this.config.expanded = !this.mergedConfig.expanded;

    if (this.mergedConfig.expanded) {
      setTimeout(() => this.searchBox.nativeElement.focus(), 200);
    }
    this.openStateToggled.emit(this.mergedConfig.expanded);
  }

  onInput(event: Event) {
    const searchText = (event.target as HTMLInputElement).value || '';
    this.lastQuery = searchText;
    const trimmed = searchText.trim();

    if (trimmed.length === 0) {
      this.searchCommitted = false;
    }
    this.searchCommit$.next(searchText);
    this.searchTextChanged.emit(searchText);

    // Only trigger search if minimum length is met
    if (searchText.length >= this.mergedConfig.minSearchLength!) {
      this.searchText$.next(searchText);
    } else {
      this.searchResults = [];
      this.loading = false;
    }
  }

  onInputFocus(): void {
    this.inputFocused = true;
    if (!this.mergedConfig.multiSelect) {
      return;
    }
    if (this.searchBox?.nativeElement) {
      this.searchBox.nativeElement.value = '';
    }
    this.lastQuery = '';
    if (this.mergedConfig.minSearchLength === 0) {
      this.loadMultiOptions('');
    }
    setTimeout(() => this.openMultiPanel());
  }

  onKeyUp(event: KeyboardEvent) {
    if (event.key === 'Enter' && !this.mergedConfig.multiSelect) {
      const searchText = (event.target as HTMLInputElement).value || '';
      const trimmed = searchText.trim();
      if (trimmed.length > 0) {
        this.searchCommitted = true;
      }
      this.searchResults = [];
      this.submit.emit(searchText);
    }
  }

  onResultSelected(event: MatAutocompleteSelectedEvent) {
    if (this.mergedConfig.multiSelect) {
      return;
    }

    const selectedResult = this.searchResults.find((r) => r.id === event.option.value);
    if (selectedResult) {
      // Find the original item that corresponds to this result
      const originalItem = this.findOriginalItem(selectedResult);
      this.resultSelected.emit({
        item: originalItem,
        mappedResult: selectedResult
      });
    }

    // Clear the search
    this.clearSearch();
  }

  clearSearch(): void {
    if (this.searchBox) {
      this.searchBox.nativeElement.value = '';
    }
    this.searchResults = [];
    this.loading = false;
    this.searchTextChanged.emit('');
  }

  onInputBlur(): void {
    if (this.mergedConfig.multiSelect) {
      this.inputFocused = false;
      this.closeMultiPanel();
      if (this.searchBox?.nativeElement) {
        this.searchBox.nativeElement.value = '';
      }
      this.lastQuery = '';
    }
    if (!this.mergedConfig.collapseOnBlurWhenEmpty || this.initiallyExpanded) {
      return;
    }
    window.setTimeout(() => {
      if (!this.mergedConfig.expanded) {
        return;
      }
      if (this.getInputText().length === 0) {
        this.config.expanded = false;
        this.openStateToggled.emit(false);
      }
    }, 150);
  }

  clearAndCollapse(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.searchCommitted = false;
    this.clearSearch();
    this.searchTextChanged.emit('');
    if (this.mergedConfig.collapseOnClear !== false && !this.initiallyExpanded) {
      this.config.expanded = false;
      this.openStateToggled.emit(false);
    }
  }

  private getInputText(): string {
    return (this.searchBox?.nativeElement?.value ?? '').trim();
  }

  private findOriginalItem(result: WsSearchResult): any {
    // This is a simple approach - in a real implementation, you might want to 
    // store the original item reference in the WsSearchResult
    return result;
  }

  // Utility method to get display fields from result
  getDisplayFields(result: WsSearchResult): string[] {
    return Object.keys(result.displayData);
  }


  get selectionSummary(): string {
    return this._selectedItems
      .map((item) => this.chipLabel(item))
      .filter((label) => !!label)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .join(', ');
  }

  private resultLabel(result: WsSearchResult): string {
    const fields = Object.keys(result?.displayData || {});
    if (!fields.length) {
      return String(result?.id ?? '');
    }
    return String(result.displayData[fields[0]] ?? '');
  }

  isSelected(result: WsSearchResult): boolean {
    return this._selectedItems.some((item) => this.sameId(item, result.id));
  }

  chipLabel(item: any): string {
    if (item == null) {
      return '';
    }
    if (typeof item !== 'object') {
      return String(item);
    }
    const mapped = this.safeMap(item);
    const fields = Object.keys(mapped?.displayData || {});
    if (!fields.length) {
      return String(mapped?.id ?? item?.name ?? item?.title ?? '');
    }
    return String(mapped.displayData[fields[0]] ?? '');
  }

  private safeMap(item: any): WsSearchResult {
    try {
      const mapped = this.resultMapper(item);
      if (mapped && mapped.id !== undefined) {
        return mapped;
      }
    } catch (error) {
      console.error('ws-searchbar resultMapper error:', error);
    }
    const id = item && typeof item === 'object' ? item.id : item;
    const label = item?.name || item?.title || item?.experienceTitle || id || '';
    return { id, displayData: { name: String(label) } };
  }

  removeSelected(item: any, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const id = item && typeof item === 'object' && 'id' in item ? item.id : item;
    this.toggleSelected(id);
  }

  private toggleSelected(id: any): void {
    const next = [...this._selectedItems];
    const index = next.findIndex((item) => this.sameId(item, id));
    if (index >= 0) {
      next.splice(index, 1);
    } else {
      const found = this.latestItems.find((item) => this.sameId(item, id));
      if (found !== undefined) {
        next.push(found);
      }
    }
    this._selectedItems = next;
    this.selectedItemsChange.emit(next);
  }

  private sameId(item: any, id: any): boolean {
    if (item && typeof item === 'object' && 'id' in item) {
      return item.id === id;
    }
    return item === id;
  }

  get shouldShowAutocomplete(): boolean {
    if (!this.searchBox?.nativeElement) {
      return false;
    }
    const searchText = this.searchBox.nativeElement.value || '';
    return searchText.length >= this.mergedConfig.minSearchLength!;
  }
}
